import React, { useState } from 'react';
import './Blog.css';
import { FaSearch, FaCalendarAlt, FaUserMd, FaClock, FaTags, FaChevronRight } from 'react-icons/fa';

const Blog = () => {
  const [activeCategory, setActiveCategory] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  
  // Sample blog data
  const blogPosts = [
    {
      id: 1,
      title: "The Future of Provider Credentialing: AI and Automation",
      excerpt: "How artificial intelligence and automation are transforming the credentialing landscape, reducing turnaround times, and improving accuracy.",
      category: "Credentialing",
      author: "Dr. Sarah Johnson",
      date: "March 5, 2025",
      readTime: "5 min read",
      tags: ["AI", "Automation", "Credentialing"],
      featured: true,
      image: "https://via.placeholder.com/600x400"
    },
    {
      id: 2,
      title: "HIPAA Compliance in the Digital Age: Protecting Patient Data",
      excerpt: "Best practices for maintaining HIPAA compliance in an increasingly digital healthcare environment, focusing on security risk assessment and mitigation.",
      category: "Security",
      author: "Michael Chen, CISSP",
      date: "February 28, 2025",
      readTime: "7 min read",
      tags: ["HIPAA", "Data Security", "Compliance"],
      featured: false,
      image: "https://via.placeholder.com/600x400"
    },
    {
      id: 3,
      title: "Streamlining Healthcare Vendor Management: A Comprehensive Guide",
      excerpt: "Exploring strategies for healthcare organizations to effectively manage vendor relationships, ensure compliance, and reduce operational risks.",
      category: "Vendor Management",
      author: "Lisa Rodriguez",
      date: "February 20, 2025",
      readTime: "6 min read",
      tags: ["Vendor Management", "Healthcare Administration"],
      featured: false,
      image: "https://via.placeholder.com/600x400"
    },
    {
      id: 4,
      title: "The Impact of the 21st Century Cures Act on Healthcare Interoperability",
      excerpt: "How the 21st Century Cures Act is driving significant changes in healthcare data sharing, interoperability standards, and patient access to information.",
      category: "Compliance",
      author: "Dr. Robert Williams",
      date: "February 15, 2025",
      readTime: "8 min read",
      tags: ["Interoperability", "Compliance", "Data Exchange"],
      featured: true,
      image: "https://via.placeholder.com/600x400"
    },
    {
      id: 5,
      title: "Improving Provider Onboarding: From Months to Days",
      excerpt: "Case study: How Memorial Health System reduced their provider onboarding time from 90 days to just 14 days using our streamlined onboarding solution.",
      category: "Onboarding",
      author: "Jennifer Smith",
      date: "February 8, 2025",
      readTime: "4 min read",
      tags: ["Onboarding", "Case Study", "Efficiency"],
      featured: false,
      image: "https://via.placeholder.com/600x400"
    },
    {
      id: 6,
      title: "Regulatory Changes Affecting Healthcare Credentialing in 2025",
      excerpt: "An overview of the latest regulatory changes and how they impact credentialing requirements for healthcare providers and organizations.",
      category: "Compliance",
      author: "Thomas Anderson, JD",
      date: "January 25, 2025",
      readTime: "6 min read",
      tags: ["Regulations", "Compliance", "Credentialing"],
      featured: false,
      image: "https://via.placeholder.com/600x400"
    }
  ];

  // Filter posts based on activeCategory and searchQuery
  const filteredPosts = blogPosts.filter(post => {
    const matchesCategory = activeCategory === 'all' || post.category.toLowerCase() === activeCategory.toLowerCase();
    const matchesSearch = post.title.toLowerCase().includes(searchQuery.toLowerCase()) || 
                          post.excerpt.toLowerCase().includes(searchQuery.toLowerCase()) ||
                          post.tags.some(tag => tag.toLowerCase().includes(searchQuery.toLowerCase()));
    return matchesCategory && matchesSearch;
  });

  // Get unique categories
  const categories = ['all', ...new Set(blogPosts.map(post => post.category))];

  // Get featured posts
  const featuredPosts = blogPosts.filter(post => post.featured);

  return (
    <div className="blog-page">
      <div className="blog-hero">
        <div className="blog-container">
          <h1 className="blog-title">Healthcare Insights & Perspectives</h1>
          <p className="blog-description">
            Expert analysis, industry trends, and practical advice for healthcare professionals, 
            organizations, and vendors navigating the complex world of credentialing and compliance.
          </p>

          <div className="blog-search">
            <input 
              type="text" 
              placeholder="Search articles..." 
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            <button className="search-button">
              <FaSearch />
            </button>
          </div>
        </div>
      </div>

      {searchQuery === '' && (
        <section className="blog-featured">
          <div className="blog-container">
            <h2 className="section-title">Featured Articles</h2>
            <div className="featured-grid">
              {featuredPosts.map(post => (
                <div className="featured-card" key={post.id}>
                  <div className="featured-image">
                    <img src={post.image} alt={post.title} />
                    <div className="featured-category">{post.category}</div>
                  </div>
                  <div className="featured-content">
                    <h3 className="featured-title">{post.title}</h3>
                    <p className="featured-excerpt">{post.excerpt}</p>
                    <div className="post-meta">
                      <span className="post-author"><FaUserMd /> {post.author}</span>
                      <span className="post-date"><FaCalendarAlt /> {post.date}</span>
                      <span className="post-read-time"><FaClock /> {post.readTime}</span>
                    </div>
                    <button className="read-more-btn">
                      Read Article <FaChevronRight />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      <section className="blog-main">
        <div className="blog-container blog-layout">
          <div className="blog-content">
            <div className="blog-categories">
              {categories.map(category => (
                <button 
                  key={category} 
                  className={`category-btn ${activeCategory === category ? 'active' : ''}`}
                  onClick={() => setActiveCategory(category)}
                >
                  {category.charAt(0).toUpperCase() + category.slice(1)}
                </button>
              ))}
            </div>

            {filteredPosts.length > 0 ? (
              <div className="blog-posts">
                {filteredPosts.map(post => (
                  <div className="blog-card" key={post.id}>
                    <div className="blog-card-image">
                      <img src={post.image} alt={post.title} />
                    </div>
                    <div className="blog-card-content">
                      <div className="blog-card-category">{post.category}</div>
                      <h3 className="blog-card-title">{post.title}</h3>
                      <p className="blog-card-excerpt">{post.excerpt}</p>
                      <div className="blog-card-meta">
                        <span className="blog-card-date"><FaCalendarAlt /> {post.date}</span>
                        <span className="blog-card-read-time"><FaClock /> {post.readTime}</span>
                      </div>
                      <div className="blog-card-tags">
                        <FaTags />
                        {post.tags.map((tag, index) => (
                          <span key={index} className="blog-tag">{tag}</span>
                        ))}
                      </div>
                      <button className="blog-read-more">Read More</button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="no-results">
                <h3>No articles found</h3>
                <p>Try adjusting your search or filter criteria</p>
              </div>
            )}
          </div>

          <div className="blog-sidebar">
            <div className="sidebar-section">
              <h3 className="sidebar-title">Subscribe to Our Newsletter</h3>
              <p className="sidebar-text">Get the latest insights and updates delivered directly to your inbox.</p>
              <div className="newsletter-form">
                <input type="email" placeholder="Your email address" />
                <button className="subscribe-btn">Subscribe</button>
              </div>
            </div>
            
            <div className="sidebar-section">
              <h3 className="sidebar-title">Popular Tags</h3>
              <div className="tags-cloud">
                {Array.from(new Set(blogPosts.flatMap(post => post.tags))).map((tag, index) => (
                  <span 
                    key={index} 
                    className="sidebar-tag"
                    onClick={() => setSearchQuery(tag)}
                  >
                    {tag}
                  </span>
                ))}
              </div>
            </div>
            
            <div className="sidebar-section">
              <h3 className="sidebar-title">Have a Question?</h3>
              <p className="sidebar-text">Our team of experts is ready to assist you with any questions about credentialing, compliance, or security.</p>
              <button className="contact-btn">Contact Us</button>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Blog;